import {
  useEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { Minus, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  REPORT_PREVIEW_HEIGHT_MAX,
  REPORT_PREVIEW_HEIGHT_MIN,
  REPORT_PREVIEW_HEIGHT_STEP,
  clampReportPreviewHeight,
  reportPreviewHeight,
} from '@/lib/patientReports/previewFrame';
import {
  REPORT_PREVIEW_ZOOM_DEFAULT,
  REPORT_PREVIEW_ZOOM_MAX,
  REPORT_PREVIEW_ZOOM_MIN,
  reportPreviewPdfSrc,
  stepPreviewZoom,
} from '@/lib/patientReports/previewPdf';
import { cn } from '@/utils/cn';

/**
 * The PDF window for a report. It opens tall enough for a page and the bottom
 * edge can be pulled to show more.
 */
export function ReportPreviewFrame({
  title,
  src,
  className,
  frameTestId = 'report-preview-frame',
  iframeTestId,
}: {
  title: string;
  src: string;
  className?: string;
  frameTestId?: string;
  iframeTestId?: string;
}) {
  const { t } = useTranslation();
  const userSized = useRef(false);
  const drag = useRef<{ y: number; height: number } | null>(null);
  const [active, setActive] = useState(false);
  const [height, setHeight] = useState(() =>
    reportPreviewHeight(typeof window === 'undefined' ? 1200 : window.innerHeight),
  );
  const [zoom, setZoom] = useState(REPORT_PREVIEW_ZOOM_DEFAULT);

  useEffect(() => {
    const onResize = () => {
      if (userSized.current) return;
      setHeight(reportPreviewHeight(window.innerHeight));
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  function onPointerDown(event: ReactPointerEvent<HTMLDivElement>) {
    if (event.button !== 0) return;
    event.preventDefault();
    drag.current = { y: event.clientY, height };
    setActive(true);
    if (typeof event.currentTarget.setPointerCapture === 'function') {
      event.currentTarget.setPointerCapture(event.pointerId);
    }
  }

  function onPointerMove(event: ReactPointerEvent<HTMLDivElement>) {
    if (!drag.current) return;
    userSized.current = true;
    setHeight(clampReportPreviewHeight(drag.current.height + (event.clientY - drag.current.y)));
  }

  function endDrag(event: ReactPointerEvent<HTMLDivElement>) {
    if (!drag.current) return;
    drag.current = null;
    setActive(false);
    const node = event.currentTarget;
    if (typeof node.hasPointerCapture === 'function' && node.hasPointerCapture(event.pointerId)) {
      node.releasePointerCapture(event.pointerId);
    }
  }

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const grow = event.key === 'ArrowDown';
    const shrink = event.key === 'ArrowUp';
    if (!grow && !shrink) return;
    event.preventDefault();
    userSized.current = true;
    const step = grow ? REPORT_PREVIEW_HEIGHT_STEP : -REPORT_PREVIEW_HEIGHT_STEP;
    setHeight((current) => clampReportPreviewHeight(current + step));
  }

  function onDoubleClick(event: { preventDefault: () => void }) {
    event.preventDefault();
    userSized.current = false;
    setHeight(reportPreviewHeight(window.innerHeight));
  }

  return (
    <div
      data-testid={frameTestId}
      className={cn('pr-preview w-full border border-ds-line bg-white', className)}
      style={{ height }}
    >
      <iframe
        data-testid={iframeTestId}
        title={title}
        src={reportPreviewPdfSrc(src, zoom)}
        className="absolute inset-0 h-full w-full bg-white"
      />
      <div className="pr-preview-zoom" role="group" aria-label={t('patientReports.zoom')}>
        <button
          type="button"
          className="fp-icon-btn is-sm"
          aria-label={t('patientReports.zoom_out')}
          disabled={zoom <= REPORT_PREVIEW_ZOOM_MIN}
          onClick={() => setZoom((current) => stepPreviewZoom(current, -1))}
        >
          <Minus className="h-4 w-4" aria-hidden />
        </button>
        <span className="pr-preview-zoom-pct">{zoom} %</span>
        <button
          type="button"
          className="fp-icon-btn is-sm"
          aria-label={t('patientReports.zoom_in')}
          disabled={zoom >= REPORT_PREVIEW_ZOOM_MAX}
          onClick={() => setZoom((current) => stepPreviewZoom(current, 1))}
        >
          <Plus className="h-4 w-4" aria-hidden />
        </button>
      </div>
      <div
        role="separator"
        aria-orientation="horizontal"
        aria-label={t('patientReports.preview_resize')}
        aria-valuemin={REPORT_PREVIEW_HEIGHT_MIN}
        aria-valuemax={REPORT_PREVIEW_HEIGHT_MAX}
        aria-valuenow={height}
        tabIndex={0}
        data-testid="report-preview-resize"
        className={cn('pr-preview-resize', active && 'is-drag')}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onKeyDown={onKeyDown}
        onDoubleClick={onDoubleClick}
      />
    </div>
  );
}
